import cn from 'classnames';
import { generateResponsiveClasses, capitalize, TResponsiveValue } from '@creditflowresearch/uilib/shared/responsive-classes';
import type {
  ToggleButtonGroupSize,
  ToggleButtonGroupTheme,
  ToggleButtonGroupFit,
  ToggleButtonGroupOrientation,
} from './types';
import ToggleButtonGroupContext from './ToggleButtonGroupContext';
import ToggleButtonGroupItem from './ToggleButtonGroupItem';
import ToggleButtonGroupBadge from './ToggleButtonGroupBadge';
import ToggleButtonGroupIcon from './ToggleButtonGroupIcon';
import { groupRadiusClassMap, groupThemeClassMap, groupFitClassMap, groupOrientationClassMap } from './constants';
import styles from './toggleButtonGroup.module.scss';

interface IProps {
  /** Currently selected item values */
  value: string[],
  /** Callback fired when an item is toggled */
  onChange: (value: string) => void,
  /** Size variant */
  size?: ToggleButtonGroupSize,
  /** Visual theme */
  theme?: ToggleButtonGroupTheme,
  /** Controls group width: 'inline' (default) or 'full' (100% with equal items). Supports responsive object. */
  fit?: TResponsiveValue<ToggleButtonGroupFit>,
  /** Stacking direction: 'horizontal' (default) or 'vertical' */
  orientation?: ToggleButtonGroupOrientation,
  /** ToggleButtonGroup.Item elements */
  children: React.ReactNode,
  /** Additional CSS class names */
  className?: string,
  /** Accessible label for the group */
  ariaLabel?: string,
  /** Test selector */
  dataTest: string,
}

const ToggleButtonGroup = ({
  value,
  onChange,
  size = 'M',
  theme = 'light',
  fit,
  orientation = 'horizontal',
  children,
  className,
  ariaLabel = 'Toggle group',
  dataTest,
}: IProps): React.ReactElement => (
  <ToggleButtonGroupContext value={ { size, theme, value, onChange } }>
    <div
      role="group"
      aria-label={ ariaLabel }
      aria-orientation={ orientation }
      className={ cn(
        styles.group,
        groupThemeClassMap[theme],
        groupRadiusClassMap[size],
        groupOrientationClassMap[orientation],
        generateResponsiveClasses(fit, 'fit', groupFitClassMap, capitalize),
        className,
      ) }
      data-test={ dataTest }
    >
      { children }
    </div>
  </ToggleButtonGroupContext>
);

ToggleButtonGroup.Item = ToggleButtonGroupItem;
ToggleButtonGroup.Badge = ToggleButtonGroupBadge;
ToggleButtonGroup.Icon = ToggleButtonGroupIcon;

export default ToggleButtonGroup;
